import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { MapPin, Globe, Lock, Eye, Users } from "lucide-react";

const RELEASE_MODES = [
  {
    id: "hidden",
    label: "Hidden",
    icon: Lock,
    desc: "Location not visible to anyone until you manually release it",
    color: "text-gray-500"
  },
  {
    id: "private",
    label: "Private (Invite Only)",
    icon: Eye,
    desc: "Only visible to invited guests or approved members",
    color: "text-amber-600"
  },
  {
    id: "waitlist",
    label: "Waitlist Release",
    icon: Users,
    desc: "Released to waitlist members first, then public after a delay",
    color: "text-blue-600"
  },
  {
    id: "public",
    label: "Public",
    icon: Globe,
    desc: "Visible to everyone immediately",
    color: "text-green-600"
  }
];

export default function LocationReleaseSettings({ location, onSave, saving }) {
  const [mode, setMode] = useState(location?.release_mode || "hidden");
  const [address, setAddress] = useState(location?.address || "");
  const [city, setCity] = useState(location?.city || "");
  const [state, setState] = useState(location?.state || "");
  const [zip, setZip] = useState(location?.zip || "");
  const [country, setCountry] = useState(location?.country || "US");
  const [placeId, setPlaceId] = useState(location?.place_id || "");
  const [lat, setLat] = useState(location?.lat ?? null);
  const [lng, setLng] = useState(location?.lng ?? null);
  const [waitlistDelay, setWaitlistDelay] = useState(location?.waitlist_delay_hours || 24);
  const [releaseDate, setReleaseDate] = useState(location?.release_date || "");

  const handlePlaceSelect = (place) => {
    setAddress(place.street_1 || "");
    setCity(place.city || "");
    setState(place.state_code || place.state || "");
    setZip(place.postal_code || "");
    setCountry(place.country_code || place.country || "US");
    setPlaceId(place.place_id || "");
    setLat(place.lat ?? null);
    setLng(place.lng ?? null);
  };

  const handleSave = () => {
    onSave({
      release_mode: mode,
      address, city, state, zip, country,
      place_id: placeId, lat, lng,
      waitlist_delay_hours: mode === "waitlist" ? waitlistDelay : null,
      release_date: releaseDate || null,
    });
  };

  return (
    <Card data-testid="location-release-settings">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <MapPin className="w-4 h-4" /> Location Settings
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Address - Google Places autocomplete fills all fields */}
        <div className="space-y-3">
          <div>
            <Label>Search Address</Label>
            <GooglePlacesInput
              mode="address"
              value={address ? `${address}${city ? `, ${city}` : ""}${state ? `, ${state}` : ""}` : ""}
              onChange={() => { /* keep autocomplete in control until selection */ }}
              onSelect={handlePlaceSelect}
              dataTestId="location-google-address"
              placeholder="Start typing your full address…"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Street</Label>
              <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="123 Main St" data-testid="location-address" />
            </div>
            <div>
              <Label>City</Label>
              <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Los Angeles" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>State</Label>
                <Input value={state} onChange={(e) => setState(e.target.value)} placeholder="CA" />
              </div>
              <div>
                <Label>Zip</Label>
                <Input value={zip} onChange={(e) => setZip(e.target.value)} placeholder="90001" />
              </div>
            </div>
          </div>
          {placeId && (
            <p className="text-xs text-gray-500" data-testid="place-id-hint">
              Linked to Google Maps · {lat?.toFixed?.(4)}, {lng?.toFixed?.(4)}
            </p>
          )}
        </div>

        {/* Release Mode */}
        <div>
          <Label className="mb-2 block">Release Mode</Label>
          <div className="grid grid-cols-2 gap-2">
            {RELEASE_MODES.map(rm => {
              const Icon = rm.icon;
              return (
                <button
                  key={rm.id}
                  onClick={() => setMode(rm.id)}
                  className={`p-3 border rounded-xl text-left transition-all ${
                    mode === rm.id ? "border-black bg-gray-50 shadow-sm" : "border-gray-200 hover:border-gray-300"
                  }`}
                  data-testid={`release-mode-${rm.id}`}
                >
                  <div className="flex items-center gap-2">
                    <Icon className={`w-4 h-4 ${rm.color}`} />
                    <span className="text-sm font-medium">{rm.label}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{rm.desc}</p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Waitlist delay */}
        {mode === "waitlist" && (
          <div>
            <Label>Waitlist Head Start (hours)</Label>
            <p className="text-xs text-gray-500 mb-2">How long before the location is released to the public</p>
            <Input
              type="number"
              value={waitlistDelay}
              onChange={(e) => setWaitlistDelay(parseInt(e.target.value) || 24)}
              min="1" max="168"
              data-testid="waitlist-delay-input"
            />
          </div>
        )}

        {/* Scheduled Release */}
        {(mode === "hidden" || mode === "waitlist") && (
          <div>
            <Label>Scheduled Release Date (optional)</Label>
            <Input
              type="datetime-local"
              value={releaseDate}
              onChange={(e) => setReleaseDate(e.target.value)}
              data-testid="release-date-input"
            />
          </div>
        )}

        <div className="flex items-center justify-between pt-2">
          <Badge variant="outline" className="capitalize">{mode}</Badge>
          <Button onClick={handleSave} disabled={saving} data-testid="save-location-btn">
            {saving ? "Saving..." : "Save Location Settings"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
